import React from "react";
import { useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import type { AdAccountSettings } from "@/hooks/useOnboardingSettings";

interface StepLaunchSettingsProps {
  adAccountId: string;
  settings: AdAccountSettings | null;
  onSave: (data: Partial<AdAccountSettings>) => Promise<any>;
  onValidChange: (valid: boolean) => void;
}

export function StepLaunchSettings({
  adAccountId,
  settings,
  onSave,
  onValidChange,
}: StepLaunchSettingsProps) {
  const [adStatus, setAdStatus] = useState<string>(settings?.default_ad_status ?? "PAUSED");
  const [multiAdvertiser, setMultiAdvertiser] = useState<boolean>(settings?.multi_advertiser_opt_in ?? false);

  // Sync from loaded settings
  useEffect(() => {
    if (settings) {
      setAdStatus(settings.default_ad_status ?? "PAUSED");
      setMultiAdvertiser(settings.multi_advertiser_opt_in ?? false);
    }
  }, [settings]);

  // Always valid
  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  // Auto-save on changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      onSave({
        default_ad_status: adStatus,
        multi_advertiser_opt_in: multiAdvertiser,
        step_launch_completed: true,
      });
    }, 500);
    return () => clearTimeout(timeout);
  }, [adStatus, multiAdvertiser]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Launch Settings</CardTitle>
        <CardDescription>
          Configure default settings for when you launch ads.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Default Ad Status */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Default Ad Launch Status</Label>
          <div className="grid grid-cols-2 gap-3">
            {/* Active card */}
            <button
              type="button"
              onClick={() => setAdStatus("ACTIVE")}
              className={cn(
                "flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors text-center",
                adStatus === "ACTIVE"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-muted-foreground/30"
              )}
            >
              <div className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full",
                adStatus === "ACTIVE" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              )}>
                <Play className="h-5 w-5" />
              </div>
              <span className="text-sm font-medium">Active</span>
              <span className="text-xs text-muted-foreground">
                Ads go live immediately when launched
              </span>
            </button>

            {/* Paused card */}
            <button
              type="button"
              onClick={() => setAdStatus("PAUSED")}
              className={cn(
                "flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors text-center",
                adStatus === "PAUSED"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-muted-foreground/30"
              )}
            >
              <div className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full",
                adStatus === "PAUSED" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              )}>
                <Pause className="h-5 w-5" />
              </div>
              <span className="text-sm font-medium">Paused</span>
              <span className="text-xs text-muted-foreground">
                Ads are created paused — review before activating
              </span>
            </button>
          </div>
        </div>

        {/* Multi-Advertiser Ads */}
        <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
          <div className="space-y-1">
            <Label className="text-sm font-medium">Multi-Advertiser Ads</Label>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Allow Meta to show your ads alongside other advertisers' ads to increase reach.
              When enabled, your ads may appear in a multi-advertiser format.
            </p>
          </div>
          <Switch
            checked={multiAdvertiser}
            onCheckedChange={setMultiAdvertiser}
          />
        </div>
      </CardContent>
    </Card>
  );
}
